import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  FlagIcon,
  EnvelopeIcon,
  Bars3BottomLeftIcon,
  ShareIcon,
} from '@heroicons/react/24/solid';
import React, { Fragment, FunctionComponent } from 'react';
import { configuration } from '../../../../services/configuration';
import { SharedLink } from '../../../../services/models/shared-link';
import * as plausible from '../../../../services/plausible';
import * as segment from '../../../../services/segment';
import { classNames } from '../../../../utils/class-names';
import { StoryCard } from '../story-card/StoryCard';

interface SharedLinkCardProps {
  sharedLink: SharedLink;
  onShareBarClick?: (storyId: number, storyUri: string) => void;
  onReadStory?: (storyId: number, storyUri: string) => void;
}

enum ShareType {
  Email,
  Twitter,
  LinkedIn,
}

export const SharedLinkCard: FunctionComponent<SharedLinkCardProps> = ({
  sharedLink,
  onShareBarClick,
  onReadStory,
}) => {
  const getShareUrl = (shareType: ShareType) => {
    const story = sharedLink.story;

    const mailBody =
      'Check out this story:\r\n\r\n' +
      story.url +
      '\r\n\r\n' +
      'Sent via Newslit - Next-generation news monitoring and research tool. https://newslit.co';

    return shareType === ShareType.Email
      ? `mailto:?subject=${encodeURI(story.title)}&body=${encodeURI(mailBody)}`
      : `${configuration.backend.baseUrl}/share?u=${encodeURI(story.url)}&t=${shareType}`;
  };

  return (
    <div className="border-b border-gray-200 px-4 py-5">
      <StoryCard key={sharedLink.id} story={sharedLink.story} />

      <div className="flex items-center justify-between pt-2.5">
        <div
          className="sharedlink-sharedby cursor-pointer whitespace-nowrap overflow-hidden"
          onClick={() => {
            if (onShareBarClick) {
              plausible.trackEvent('Story ShareBar Clicked', {
                shares: `${sharedLink.shareCount}`,
              });
              segment.trackEvent('Story ShareBar Clicked', {
                shares: `${sharedLink.shareCount}`,
              });

              onShareBarClick(sharedLink.story.id, sharedLink.story.url);
            }
          }}
        >
          <span className="mr-2 inline-block max-h-[30px] rounded-[5px] bg-gray-200 px-[0.6em] py-[0.35em] text-center align-middle text-[1.2em] font-bold leading-none text-gray-700">
            {sharedLink.shareCount}
          </span>

          {sharedLink.externalUsers.map((externalUser, index) => {
            const imgKey = `${sharedLink.id}-${externalUser.username}-${index}`;

            return (
              <img
                key={imgKey}
                src={externalUser.imageUrl}
                alt={externalUser.username}
                className="inline h-7 w-7 rounded-full ring-2 ring-white"
                loading="lazy"
              />
            );
          })}
        </div>

        <div className="sharedlink-actions">
          <Menu as="div" className="relative inline-block text-left">
            <div>
              <MenuButton className="-m-2 p-1.5 border border-gray-200 rounded-full flex items-center text-gray-400 hover:text-gray-600 hover:bg-gray-50">
                <span className="sr-only">Open options</span>
                <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
              </MenuButton>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black/5 focus:outline-hidden">
                <div className="py-1">
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          plausible.trackEvent('SharedLink Menu Clicked', {
                            option: 'Share via Twitter',
                          });
                          window.open(getShareUrl(ShareType.Twitter), '_blank');
                        }}
                        className={classNames(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex items-center w-full px-4 py-2 text-sm'
                        )}
                      >
                        <ShareIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <span>Share via Twitter</span>
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          plausible.trackEvent('SharedLink Menu Clicked', {
                            option: 'Share via Email',
                          });
                          window.open(getShareUrl(ShareType.Email), '_blank');
                        }}
                        className={classNames(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex items-center w-full px-4 py-2 text-sm'
                        )}
                      >
                        <EnvelopeIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <span>Share via Email</span>
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          plausible.trackEvent('SharedLink Menu Clicked', {
                            option: 'Read Article',
                          });

                          if (onReadStory) {
                            plausible.trackEvent('Story Reader Clicked', {
                              story: sharedLink.story.url,
                            });
                            segment.trackEvent('Story Reader Clicked', {
                              story: sharedLink.story.url,
                            });

                            onReadStory(sharedLink.story.id, sharedLink.story.url);
                          }
                        }}
                        className={classNames(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex items-center w-full px-4 py-2 text-sm'
                        )}
                      >
                        <Bars3BottomLeftIcon
                          className="mr-3 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        <span>Read Article</span>
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          plausible.trackEvent('SharedLink Menu Clicked', {
                            option: 'Report content',
                          });

                          const my = window as any;
                          if (my.hasOwnProperty('Intercom')) {
                            my['Intercom']('show');
                          }
                        }}
                        className={classNames(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex items-center w-full px-4 py-2 text-sm'
                        )}
                      >
                        <FlagIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <span>Report content</span>
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};
